import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function Loader({ label = "Loading...", className }: { label?: string; className?: string }) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 py-10", className)}>
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#e2e8f0] border-t-[#2563eb]" />
      <p className="text-[13px] text-[#6c757d]">{label}</p>
    </div>
  );
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 py-12 text-center", className)}>
      {icon ? <div className="text-[64px] opacity-30 leading-none">{icon}</div> : null}
      <h3 className="text-[17px] font-bold text-[#1a1a1a]">{title}</h3>
      {description ? <p className="max-w-[420px] text-[14px] text-[#6c757d]">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

interface ErrorBlockProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorBlock({ title = "Something went wrong", message, onRetry, className }: ErrorBlockProps) {
  return (
    <div className={cn("rounded-[10px] border border-[#fecaca] bg-[#fef2f2] p-4", className)}>
      <p className="text-[13px] font-semibold text-[#991b1b]">{title}</p>
      <p className="mt-1 text-[13px] text-[#991b1b]">{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-[10px] border border-[#fecaca] bg-white px-4 py-2 text-[13px] font-semibold text-[#991b1b] hover:bg-[#fef2f2]"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

interface InlineAlertProps {
  tone?: "info" | "warning" | "success" | "danger";
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function InlineAlert({ tone = "info", icon, children, className }: InlineAlertProps) {
  const toneStyles = {
    info: "border-[#bfdbfe] bg-[#e6f1fb] text-[#1d4ed8]",
    warning: "border-[#fed7aa] bg-[#fffbeb] text-[#78350f]",
    success: "border-[#bbf7d0] bg-[#f0fdf4] text-[#065f46]",
    danger: "border-[#fecaca] bg-[#fef2f2] text-[#991b1b]",
  }[tone];

  return (
    <div className={cn("flex items-start gap-3 rounded-[10px] border p-4 text-[13px]", toneStyles, className)}>
      {icon ? <span className="text-base leading-tight">{icon}</span> : null}
      <div className="flex-1">{children}</div>
    </div>
  );
}
